import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getAiChatBot,
  getAiChatBotSources,
  deleteAiChatBotSource,
  postAiChatBotIngestWebsite,
  getAiChatBotFaqs,
  patchAiChatBotSource,
  postAiChatBotSource,
  getAiPersonas,
  createAiPersona,
  updateAiPersona as updateAiPersonaApi,
  deleteAiPersona as deleteAiPersonaApi,
  createAiPersonaVideo,
  completeAiPersonaVideoUpload,
  generateAiChatbotFaq,
} from 'shared/api';
import {
  AIChatBot,
  QADataSource,
  WebsiteDataSource,
  AIChatBotDataSourceType,
  PolymorphicAIChatBotDataSource,
  AiChatbotJobStatus,
} from 'types/ai-chatbot';
import type { Store } from './index';
import { AIPersona, AIPersonaAdd, AIPersonaVideoType } from 'types/ai-persona';
import { uploadSignedPost } from 'shared/utils/s3';
import { sleep } from 'shared/utils/helpers';

export const initAiChatBot = createAsyncThunk(
  'aiChat/initAiChatBot',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAiChatBot();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAllAiChatBotSources = createAsyncThunk(
  'aiChat/fetchAllAiChatBotSources',
  async (id: number, { rejectWithValue }) => {
    try {
      const fetchAllSources = async (
        id: number,
        allSources: PolymorphicAIChatBotDataSource[] = [],
        nextPage = 1,
      ): Promise<WebsiteDataSource[]> => {
        const response = await getAiChatBotSources(id, nextPage);
        const newSources = response.data.items;
        const updatedSources = [...allSources, ...newSources];

        if (response.data.has_next) {
          return fetchAllSources(id, updatedSources, nextPage + 1);
        } else {
          return updatedSources.filter(
            s => s.sourceType === AIChatBotDataSourceType.website_link,
          ) as WebsiteDataSource[];
        }
      };

      const websites = await fetchAllSources(id);
      return websites;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSource = createAsyncThunk(
  'aiChat/deleteSource',
  async (
    { chatBotId, sourceId }: { chatBotId: number; sourceId: number | string },
    { rejectWithValue },
  ) => {
    try {
      await deleteAiChatBotSource(chatBotId, sourceId);
      return sourceId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAndProcessWebsite = createAsyncThunk(
  'aiChat/fetchAndProcessWebsite',
  async ({ id, url }: { id: number; url: string }) => {
    await postAiChatBotIngestWebsite(id, url);
    let status: AiChatbotJobStatus = 'in_progress';
    let chatBot: AIChatBot;
    while (status === 'in_progress') {
      chatBot = (await getAiChatBot()).data;
      status = chatBot.jobs.find(job => job.jobType == 'scrape')?.status || 'in_progress';
      if (status !== 'in_progress') {
        break;
      }
      await sleep(3000);
    }
  },
);

export const generateFaqFromWebsite = createAsyncThunk(
  'aiChat/generateFaqFromWebsite',
  async (id: number) => {
    await generateAiChatbotFaq(id);
    let status: AiChatbotJobStatus = 'in_progress';
    let chatBot: AIChatBot;
    while (status === 'in_progress') {
      chatBot = (await getAiChatBot()).data;
      status =
        chatBot.jobs.find(job => job.jobType == 'generate_faq')?.status || 'in_progress';
      if (status !== 'in_progress') {
        break;
      }
      await sleep(3000);
    }
  },
);

export const fetchAllAiChatBotFaqs = createAsyncThunk(
  'aiChat/fetchAllAiChatBotFaqs',
  async (id: number, { rejectWithValue }) => {
    try {
      const fetchFaqs = async (
        id: number,
        allFaqs: QADataSource[] = [],
        nextPage = 1,
        perPage = 100,
      ): Promise<QADataSource[]> => {
        const response = await getAiChatBotFaqs(id, nextPage, perPage);
        const newFaqs = response.data.items;
        const updatedFaqs = [...allFaqs, ...newFaqs];

        if (response.data.hasNext) {
          return fetchFaqs(id, updatedFaqs, nextPage + 1, perPage);
        } else {
          return updatedFaqs;
        }
      };

      const faqs = await fetchFaqs(id);
      return faqs;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateQaItem = createAsyncThunk(
  'aiChat/updateQaItem',
  async (
    {
      chatBotId,
      qaItemId,
      question,
      answer,
    }: {
      chatBotId: number;
      qaItemId: number | string;
      question: string;
      answer: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        question,
        answer,
        ai_chatbot_id: chatBotId,
        source_type: AIChatBotDataSourceType.question_answer,
      };
      const response = await patchAiChatBotSource(chatBotId, qaItemId, payload);
      return { ...response.data, _id_to_update: qaItemId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addQaItem = createAsyncThunk(
  'aiChat/addQaItem',
  async (
    {
      chatBotId,
      question,
      answer,
    }: {
      chatBotId: number;
      question: string;
      answer: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        source_type: AIChatBotDataSourceType.question_answer,
        question,
        answer,
        ai_chatbot_id: chatBotId,
      };
      const response = await postAiChatBotSource(
        chatBotId,
        (payload as unknown) as QADataSource,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAiPersonas = createAsyncThunk(
  'aiChat/fetchAiPersonas',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAiPersonas();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAiPersona = createAsyncThunk(
  'aiChat/addAiPersona',
  async (data: AIPersonaAdd, { rejectWithValue }) => {
    try {
      const response = await createAiPersona(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAiPersona = createAsyncThunk(
  'aiChat/updateAiPersona',
  async ({ id, data }: { id: number; data: Partial<AIPersona> }, { rejectWithValue }) => {
    try {
      const response = await updateAiPersonaApi(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteAiPersona = createAsyncThunk(
  'aiChat/deleteAiPersona',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteAiPersonaApi(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addAiPersonaVideo = createAsyncThunk(
  'aiChat/addAiPersonaVideo',
  async (
    data: {
      file: Blob;
      aiPersonaId: number;
      videoType: AIPersonaVideoType;
      mimetype: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await createAiPersonaVideo({
        aiPersonaId: data.aiPersonaId,
        videoType: data.videoType,
        mimetype: data.mimetype,
      });

      await uploadSignedPost(data.file, response.data.data);

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const completeUpload = createAsyncThunk(
  'aiChat/completeAiPersonaVideoUpload',
  async (personaId: number, { rejectWithValue }) => {
    try {
      const response = await completeAiPersonaVideoUpload(personaId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export type AiChatState = {
  aiChatBot: AIChatBot | null;
  websites: WebsiteDataSource[] | undefined;
  qa: QADataSource[] | undefined;
  aiPersonas: AIPersona[];
  isChatBotLoading: boolean;
  isChatBotSourcesLoading: boolean;
  isChatBotFaqGenerating: boolean;
  isAiPersonasLoading: boolean;
  error: string | null;
  generatedAnswer: {
    question: string;
    answer: string;
    status: 'success' | 'error';
  } | null;
};

const initialState: AiChatState = {
  aiChatBot: null,
  websites: undefined,
  qa: undefined,
  aiPersonas: [],
  isChatBotLoading: false,
  isChatBotFaqGenerating: false,
  isChatBotSourcesLoading: false,
  isAiPersonasLoading: false,
  error: null,
  generatedAnswer: null,
};

const resetState = (state: AiChatState) => {
  state.aiChatBot = null;
  state.websites = undefined;
  state.qa = undefined;
  state.aiPersonas = [];
  state.isChatBotLoading = false;
  state.isChatBotSourcesLoading = false;
  state.isChatBotFaqGenerating = false;
  state.isAiPersonasLoading = false;
  state.error = null;
  state.generatedAnswer = null;
};

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    setGeneratedAnswer: (state, action) => {
      state.generatedAnswer = action.payload;
    },
    setAiChatBot: (state, action) => {
      state.aiChatBot = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // initAiChatBot
      .addCase(initAiChatBot.pending, state => {
        state.isChatBotLoading = true;
      })
      .addCase(initAiChatBot.fulfilled, (state, action) => {
        resetState(state);
        state.aiChatBot = action.payload;
      })
      .addCase(initAiChatBot.rejected, (state, action) => {
        resetState(state);
        state.error = action.payload as string;
      })

      // fetchAllAiChatBotSources
      .addCase(fetchAllAiChatBotSources.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAllAiChatBotSources.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.websites = action.payload;
      })
      .addCase(fetchAllAiChatBotSources.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // deleteSource
      .addCase(deleteSource.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(deleteSource.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.websites = state.websites?.filter(website => website.id !== action.payload);
        state.qa = state.qa?.filter(qaItem => qaItem.id !== action.payload);
      })
      .addCase(deleteSource.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // fetchAndProcessWebsite
      .addCase(fetchAndProcessWebsite.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAndProcessWebsite.fulfilled, state => {
        state.isChatBotSourcesLoading = false;
      })
      .addCase(fetchAndProcessWebsite.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.error.message || 'Failed to fetch and process website';
      })

      // fetchAllAiChatBotFaqs
      .addCase(fetchAllAiChatBotFaqs.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(fetchAllAiChatBotFaqs.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.qa = action.payload;
      })
      .addCase(fetchAllAiChatBotFaqs.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // generateFaqFromWebsite
      .addCase(generateFaqFromWebsite.pending, state => {
        state.isChatBotFaqGenerating = true;
      })
      .addCase(generateFaqFromWebsite.fulfilled, state => {
        state.isChatBotFaqGenerating = false;
      })
      .addCase(generateFaqFromWebsite.rejected, (state, action) => {
        state.isChatBotFaqGenerating = false;
        state.error = action.error.message || 'Failed to generate FAQ';
      })

      // updateQaItem
      .addCase(updateQaItem.pending, state => {
        state.isChatBotSourcesLoading = true;
      })
      .addCase(updateQaItem.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        const updatedQaItem = action.payload;
        state.qa = state.qa?.map(item =>
          item.id === updatedQaItem._id_to_update ? updatedQaItem : item,
        );
      })
      .addCase(updateQaItem.rejected, (state, action) => {
        state.isChatBotSourcesLoading = false;
        state.error = action.payload as string;
      })

      // addQaItem
      .addCase(addQaItem.fulfilled, (state, action) => {
        state.isChatBotSourcesLoading = false;
        const newQaItem = action.payload as QADataSource;
        state.qa = state.qa ? [newQaItem, ...state.qa] : [newQaItem];
      })

      // fetchAiPersonas
      .addCase(fetchAiPersonas.pending, state => {
        state.isAiPersonasLoading = true;
      })
      .addCase(fetchAiPersonas.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        state.aiPersonas = action.payload;
      })
      .addCase(fetchAiPersonas.rejected, state => {
        state.isAiPersonasLoading = false;
      })

      // addAiPersona
      .addCase(addAiPersona.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        state.aiPersonas = [...state.aiPersonas, action.payload];
      })

      // updateAiPersona
      .addCase(updateAiPersona.fulfilled, (state, action) => {
        state.isAiPersonasLoading = false;
        const updatedPersona = action.payload;
        state.aiPersonas = state.aiPersonas.map(persona =>
          persona.id === updatedPersona.id ? updatedPersona : persona,
        );
      })

      // deleteAiPersona
      .addCase(deleteAiPersona.fulfilled, (state, action) => {
        state.aiPersonas = state.aiPersonas.filter(
          persona => persona.id !== action.payload,
        );
      })

      // completeAiPersonaVideoUpload
      .addCase(completeUpload.fulfilled, (state, action) => {
        const updatedPersona = action.payload;
        state.aiPersonas = state.aiPersonas.map(persona =>
          persona.id === updatedPersona.id ? updatedPersona : persona,
        );
      });
  },
});

export const { setGeneratedAnswer, setAiChatBot } = aiChatSlice.actions;

export async function initAiChat(store: Store) {
  await store.dispatch(initAiChatBot());
}

export default aiChatSlice.reducer;
